import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { Slide } from '@/components/slides/Slide';
import { SlideComponentCreationLayer } from '@/components/slides/SlideComponentCreationLayer';
import { SlideContainer } from '@/components/slides/SlideContainer';
import { LessonPrintingDialog } from '@/containers/LessonPrintingDialog/LessonPrintingDialog';
import { LessonSharingDialog } from '@/containers/LessonSharingDialog/LessonSharingDialog';
import { LessonStartDialog } from '@/containers/LessonStartDialog/LessonStartDialog';
import { capture } from '@/handlers/error';
import { useAudioGenerator } from '@/hooks/useAudioGenerator/useAudioGenerator';
import { useClassroom } from '@/hooks/useClassroom/useClassroom';
import { useLessons } from '@/hooks/useLessons/useLessons';
import { useMedia } from '@/hooks/useMedia/useMedia';
import { Lesson } from '@/models/lesson';
import { EditorProvider } from '@/providers/editor/EditorProvider';
import { useSession } from '@/providers/session/SessionProvider';
import { isSlideComponentAudio, SlideComponent, SlideComponentFileModel, Slide as SlideData, SlideTemplateType } from '@/types/slide';
import { Box, Button, ButtonGroup, styled } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import { LessonEditorAddNewSlidePopover } from './components/LessonEditorAddNewSlidePopover';
import { LessonEditorHeader } from './components/LessonEditorHeader';
import { LessonEditorSlideList } from './components/LessonEditorSlideList';
import { SmartSlideGeneratorDialog } from './experiments/SmartSlideGenrationDialog/SmartSlideGenerationDialog';
import { SlideState } from './types';

const LessonEditorAddButtonBox = styled(Box)(({ theme }) => [
    {
        padding: theme.spacing(1),
        paddingBottom: theme.spacing(2),
        backgroundColor: (theme.vars || theme).palette.grey[200],
    },
    theme.applyStyles('dark', {
        backgroundColor: (theme.vars || theme).palette.grey[800],
    }),
]);

export type SelectedSlideType = { index: number; editorData: SlideData | undefined; editorState: SlideState | undefined };

export default function LessonEditorPage() {
    // general hooks
    const { t } = useTranslation();
    const { isMobile } = useMedia();
    const navigate = useNavigate();
    const notifications = useNotifications();
    const { session } = useSession();
    const { start } = useClassroom();
    const location = useLocation();
    const { generateAudio } = useAudioGenerator();

    // init state
    const params = useParams();
    const lessonId = params.lessonId;
    const { lessons, update } = useLessons({ id: { equals: lessonId } });
    const lesson = useMemo(() => (lessons ? lessons[0] : undefined), [lessons]);
    const editable = useMemo(() => {
        const isOwner = lesson?.isOwnedBy(session?.user?.teacherProfile ?? { id: '' });
        return (!lesson?.latestLessonDraft?.published && isOwner) ?? false;
    }, [lesson, session?.user?.teacherProfile]);

    // ui state
    const [pickingComponentType, setPickingComponentType] = useState<string | null>(null);
    const [selectedSlide, setSelectedSlide] = useState<SelectedSlideType>({
        index: 0,
        editorData: undefined,
        editorState: undefined,
    });
    const [lessonStartDialogData, setLessonStartDialogData] = React.useState<Lesson | null>(null);
    const [lessonSharingDialogData, setLessonSharingDialogData] = React.useState<Lesson | null>();
    const [lessonPrintingDialogData, setLessonPrintingDialogData] = React.useState<Lesson | null>();
    const [newSlidePopoverAnchorEl, setNewSlidePopoverAnchorEl] = useState<HTMLElement | null>(null);
    const [smartSlideGeneratorDialogOpen, setSmartSlideGeneratorDialogOpen] = useState(false);

    // ref
    const addButtonGroupRef = React.useRef<HTMLDivElement>(null);

    const MemoizedSlide = useMemo(() => {
        return <>{selectedSlide && selectedSlide?.editorData?.data && <Slide components={selectedSlide?.editorData?.data} state={selectedSlide.editorState} preview={!editable} />}</>;
    }, [editable, selectedSlide]);

    const handleAudioGeneration = useCallback(
        async (
            slideComponents: SlideComponent[],
            createSlideFile: (slideId: string, file: File) => Promise<SlideComponentFileModel | null>,
        ): Promise<{ components: SlideComponent[]; fileIds: string[] }> => {
            const fileIds: string[] = [];
            for (const component of slideComponents) {
                // Check if the component is an audio component
                if (isSlideComponentAudio(component)) {
                    // Iterate through each audio file in the component
                    for (let i = 0; i < component.content.audio.length; i++) {
                        const audioFile = component.content.audio[i];

                        // Only generate audio if it has AI configuration (voice and text)
                        if (audioFile && audioFile.ai?.voice && audioFile.ai?.text) {
                            try {
                                const generatedAudioFile = await generateAudio(audioFile.ai.voice, audioFile.ai.text);

                                const file = await createSlideFile('', generatedAudioFile);
                                if (!file) {
                                    throw new Error('Failed to upload generated audio file.');
                                }
                                fileIds.push(file.id);
                                component.content.audio[i] = {
                                    ...component.content.audio[i],
                                    id: file.id,
                                    url: file.url,
                                    filename: file.filename,
                                };
                            } catch (error) {
                                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                                capture(error);
                            }
                        }
                    }
                }
            }
            return { components: slideComponents, fileIds };
        },
        [generateAudio, notifications],
    );

    return (
        <EditorProvider lessonVersionId={lesson?.latestLessonDraft.id} selectedSlide={selectedSlide} setSelectedSlide={setSelectedSlide}>
            {({ slideListData, loading, reorderSlide, addSlide, deleteSlide, createComponent, duplicateSlide, createSlideFile }) => {
                const minScale = 0.075;
                const padding = isMobile ? 0 : 2;
                const isPickingComponent = !!pickingComponentType;

                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'column',
                        }}
                    >
                        <LessonEditorHeader
                            lesson={lesson}
                            editable={editable}
                            onBack={() => {
                                const fromLessons = location.state?.prevPath.includes('/lessons');
                                if (fromLessons) {
                                    navigate(-1);
                                } else {
                                    navigate('/lessons', { viewTransition: true });
                                }
                            }}
                            onTitleChange={async (title) => {
                                try {
                                    if (lesson) {
                                        await update(lesson, { name: title });
                                    }
                                } catch (error) {
                                    notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                                    capture(error);
                                }
                            }}
                            onStartLesson={async (lesson) => {
                                setLessonStartDialogData(lesson);
                            }}
                            onCreateComponent={async (componentType: string) => {
                                setPickingComponentType(componentType);
                            }}
                            onShareClick={() => {
                                setLessonSharingDialogData(lesson);
                            }}
                            onPrintClick={() => {
                                setLessonPrintingDialogData(lesson);
                            }}
                        />

                        <div style={{ flexGrow: 1, flexBasis: 0, overflow: 'auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
                            <Box
                                sx={(theme) => ({
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRight: 1,
                                    borderColor: (theme.vars || theme).palette.divider,
                                    boxShadow: theme.shadows[1],
                                })}
                            >
                                <LessonEditorSlideList
                                    editable={editable}
                                    slides={slideListData}
                                    loading={loading}
                                    onReorder={async (items) => {
                                        try {
                                            await reorderSlide(items);
                                        } catch (error) {
                                            notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                                            capture(error);
                                        }
                                    }}
                                    onDuplicateSlide={async (slideIndex: number) => {
                                        if (slideListData && slideListData[slideIndex]) {
                                            try {
                                                await duplicateSlide(slideListData[slideIndex]);
                                            } catch (error) {
                                                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                                                capture(error);
                                            }
                                        }
                                    }}
                                    onDeleteSlide={async (slideIndex: number) => {
                                        try {
                                            await deleteSlide(slideIndex);
                                        } catch (error) {
                                            notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                                            capture(error);
                                        }
                                    }}
                                    selectedSlideIndex={selectedSlide.index}
                                    onSlideSelectedHandler={(index) => {
                                        if (slideListData[index]) {
                                            setSelectedSlide({ index, editorData: slideListData[index], editorState: undefined });
                                        }
                                    }}
                                />

                                <LessonEditorAddButtonBox>
                                    <ButtonGroup
                                        variant='contained'
                                        sx={{
                                            '& .MuiButtonGroup-firstButton': {
                                                borderColor: 'transparent',
                                                borderRightColor: 'divider',
                                            },
                                        }}
                                        ref={addButtonGroupRef}
                                        fullWidth
                                    >
                                        <Button
                                            disabled={!editable}
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                                setNewSlidePopoverAnchorEl(addButtonGroupRef.current);
                                            }}
                                        >
                                            {t('lesson.actions.newSlide')}
                                        </Button>
                                        <Button
                                            disabled={!editable}
                                            onClick={() => {
                                                setSmartSlideGeneratorDialogOpen(true);
                                            }}
                                            sx={{
                                                '&:hover': {
                                                    color: (theme) => (theme.vars || theme).palette.primary.main,
                                                },
                                            }}
                                            fullWidth={false}
                                        >
                                            <AutoAwesomeIcon fontSize='small' />
                                        </Button>
                                    </ButtonGroup>
                                    <LessonEditorAddNewSlidePopover
                                        anchorEl={newSlidePopoverAnchorEl}
                                        onTemplateClick={async (type: SlideTemplateType) => {
                                            try {
                                                if (type === SlideTemplateType.blank) {
                                                    await addSlide();
                                                }
                                            } catch (error) {
                                                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                                                capture(error);
                                            }
                                        }}
                                        onClose={() => {
                                            setNewSlidePopoverAnchorEl(null);
                                        }}
                                    />
                                </LessonEditorAddButtonBox>
                            </Box>

                            <Box
                                sx={(theme) => ({
                                    p: padding,
                                    flex: 1,
                                    display: 'flex',
                                    overflowY: 'hidden',
                                    overflowX: isMobile ? 'auto' : 'hidden',
                                    backgroundColor: (theme.vars || theme).palette.grey[300],
                                    ...theme.applyStyles('dark', {
                                        backgroundColor: (theme.vars || theme).palette.grey[700],
                                    }),
                                })}
                            >
                                <SlideContainer padding={isMobile ? 16 : undefined} minScale={minScale} navigation={false}>
                                    {MemoizedSlide}

                                    {isPickingComponent && (
                                        <SlideComponentCreationLayer
                                            pickingComponentType={pickingComponentType}
                                            onCreateComponent={async (pickingComponentType, position) => {
                                                try {
                                                    await createComponent(pickingComponentType, position);
                                                } catch (error) {
                                                    notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                                                    capture(error);
                                                }
                                                setPickingComponentType(null);
                                            }}
                                        />
                                    )}
                                </SlideContainer>
                            </Box>
                        </div>

                        {lessonStartDialogData && (
                            <LessonStartDialog
                                payload={undefined}
                                open={true}
                                onClose={async (result) => {
                                    if (result) {
                                        try {
                                            const classroom = await start(lessonStartDialogData, result.class, result.name, result.passlock);
                                            navigate(`/classrooms/${classroom.id}`, { state: classroom, viewTransition: true });
                                        } catch (error) {
                                            notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                                            capture(error);
                                            return;
                                        }
                                    }

                                    setLessonStartDialogData(null);
                                }}
                            />
                        )}

                        {lessonSharingDialogData && (
                            <LessonSharingDialog
                                fullWidth
                                maxWidth='sm'
                                open={true}
                                onClose={() => {
                                    setLessonSharingDialogData(null);
                                }}
                                onCancel={() => {
                                    setLessonSharingDialogData(null);
                                }}
                                onShared={() => {
                                    setLessonSharingDialogData(null);
                                }}
                                lesson={lessonSharingDialogData}
                            />
                        )}

                        {lessonPrintingDialogData && (
                            <LessonPrintingDialog
                                maxWidth={false}
                                open={true}
                                onClose={() => setLessonPrintingDialogData(null)}
                                onCancel={() => setLessonPrintingDialogData(null)}
                                lesson={lessonPrintingDialogData}
                            />
                        )}

                        {smartSlideGeneratorDialogOpen && (
                            <SmartSlideGeneratorDialog
                                onClose={() => setSmartSlideGeneratorDialogOpen(false)}
                                onCancel={() => setSmartSlideGeneratorDialogOpen(false)}
                                onSave={async (components) => {
                                    try {
                                        const handledData = await handleAudioGeneration(components, createSlideFile);
                                        await addSlide(handledData.components, handledData.fileIds);
                                        setSmartSlideGeneratorDialogOpen(false);
                                    } catch (error) {
                                        notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                                        capture(error);
                                    }
                                }}
                                lesson={lesson}
                                open={true}
                            />
                        )}
                    </Box>
                );
            }}
        </EditorProvider>
    );
}
